// import 'hammerjs';
import {
  enableProdMode,
  LOCALE_ID,
  provideAppInitializer,
  inject,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { environment } from './environments/environment';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { I18nService } from './app/services/i18n.service';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { PaginatorI18n } from './app/utils/paginator.i18n';
import { DatePipe } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withJsonpSupport,
  HttpClient,
  HttpBackend,
  withNoXsrfProtection,
} from '@angular/common/http';
import { GLOBAL_HTTP_INTERCEPTORS } from './app/modules/global/global-http.interceptors';
import { InitPermissionService } from './app/core/auth/services/init-permission.service';
import { provideRouter, Router } from '@angular/router';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { bootstrapApplication } from '@angular/platform-browser';
import { routesProgrammatic } from './app/app-routing';
import { NgxPermissionsModule } from 'ngx-permissions';
import {
  provideTranslateService,
  TranslateLoader,
  MissingTranslationHandler,
  TranslateCompiler,
  MissingTranslationHandlerParams,
} from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { FuseModule } from '@fuse/fuse.module';
import { fuseConfig } from 'app/fuse-config';
import { ToastNoAnimationModule } from 'ngx-toastr';
import { AppComponent } from './app/app.component';
import { createErrorHandler, TraceService } from '@sentry/angular';
import { map } from 'rxjs';
import { provideTimeago, TimeagoIntl } from 'ngx-timeago';
import { ErrorStateMatcher, provideNativeDateAdapter } from '@angular/material/core';
import { FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import {
  providePusherResources,
  PusherResourcesConfigInterface,
} from 'app/modules/global/services/pusher-resources.service';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';

if (environment.production) {
  enableProdMode();
  if (window) {
    // disabled all console log and info to production environment
    window.console.log = window.console.info = () => {};
  }
}

const InitPermissionsFactory = (permissions: InitPermissionService) => {
  return () => permissions.load();
};

class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    const placeholder = false; //'??????';
    let untranslatedText = params.key;
    if (typeof untranslatedText === 'string') {
      untranslatedText = untranslatedText.split('@@')[0];
    }
    return placeholder || untranslatedText;
  }
}

class MyTranslateLoader implements TranslateLoader {
  constructor(
    private readonly http: HttpClient,
    private readonly prefix: string = 'assets/i18n/',
    private readonly suffix: string = '.json',
  ) {}

  public getTranslation(lang: string): any {
    return this.http.get(`${this.prefix}${lang}${this.suffix}`).pipe(map((result) => this.process(result)));
  }

  private process(object: object) {
    // object[key]
    const placeholder = false; // '######';
    return (
      Object.keys(object)
        .filter((key) => object.hasOwnProperty(key) && object[key] !== '')
        //* .reduce((result, key) => (result[key] = typeof object[key] === 'object' ? this.process(object[key]) : (placeholder ? placeholder : object[key]), result), {});
        .reduce((result, key) => {
          result[key] = typeof object[key] === 'object' ? this.process(object[key]) : placeholder || object[key];
          return result;
        }, {})
    );
  }
}

const HttpLoaderFactory = (httpBackend: HttpBackend) => {
  // USE HttpBackend (NOT HttpClient) TO LOAD TRANSLATIONS: HttpClient goes through
  // HTTP_INTERCEPTORS, and some interceptors (TokenInterceptor, ErrorHttpInterceptor)
  // depend on AuthenticationService -> AuthRepositoryService -> HttpClient, which
  // causes NG0200 circular dependency when this loader is the first HttpClient consumer.
  return new MyTranslateLoader(new HttpClient(httpBackend));
};

class CustomShowOnTouchedErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    return !!(control?.invalid && (control.touched || form?.submitted));
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    InitPermissionService,
    provideAppInitializer(() => {
      const initializerFn = InitPermissionsFactory(inject(InitPermissionService));
      return initializerFn();
    }),

    provideRouter(routesProgrammatic),
    ...GLOBAL_HTTP_INTERCEPTORS,
    provideTimeago(),
    TimeagoIntl,
    provideTranslateService({
      lang: I18nService.getDefaultLanguage(),
      fallbackLang: I18nService.getMainLanguage(),
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpBackend],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler,
      },
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatCompiler,
      },
    }),
    importProvidersFrom(
      NgxPermissionsModule.forRoot(),
      // Fuse modules
      FuseModule.forRoot(fuseConfig),
      //third part
      ToastNoAnimationModule.forRoot({
        timeOut: 3000,
        positionClass: 'toast-bottom-center',
        autoDismiss: true,
        closeButton: true,
        progressBar: true,
        toastClass: 'ngx-toastr fade-in',
      }),
    ),
    // { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline', subscriptSizing: 'dynamic' } },
    I18nService,
    {
      provide: MatPaginatorIntl,
      deps: [I18nService],
      useFactory: (_i18nService: I18nService) => new PaginatorI18n(_i18nService).getPaginatorIntl(),
    },
    DatePipe,
    {
      provide: LOCALE_ID,
      useValue: I18nService.getDefaultLanguage(),
    },
    {
      provide: TraceService,
      deps: [Router],
      useValue: undefined,
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler({
        showDialog: false,
        logErrors: !environment.production,
      }),
    },
    provideHttpClient(
      withInterceptorsFromDi(),
      withJsonpSupport(),
      // withXsrfConfiguration({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' }),
      withNoXsrfProtection(), // Disable default behavior to avoid conflicts
    ),
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        hideMultipleSelectionIndicator: true,
      },
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        stretchTabs: false,
        alignTabs: 'start',
      },
    },
    { provide: ErrorStateMatcher, useClass: CustomShowOnTouchedErrorStateMatcher },

    provideZoneChangeDetection({
      // MERGE MULTIPLE EVENTS INTO A SINGLE ZONE CHANGE
      eventCoalescing: true,
      // Deliberately no runCoalescing: it defers CD for EVERY zone task (incl. HTTP
      // responses) to a requestAnimationFrame/setTimeout race, which browsers throttle
      // when the tab/window isn't focused. Turned out not to be the cause of the
      // "stuck loading" bug (see AppComponent/CpanelComponent), but no reason to keep it.
    }),
    provideNativeDateAdapter(),
    providePusherResources(<PusherResourcesConfigInterface>environment.soketi_connections.resource),
  ],
}).catch((err) => console.log(err));
